import axios from 'axios';
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload, FileText, Users, Megaphone, CheckCircle, AlertCircle, DollarSign } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export const ImportHistorique = () => {
  const [activeTab, setActiveTab] = useState("heures");
  const [loading, setLoading] = useState(false);
  
  // Import Heures Gestion Minute
  const [heuresData, setHeuresData] = useState("");
  const [anneeHeures, setAnneeHeures] = useState("2024");
  
  // Import Marketing
  const [soumissionsData, setSoumissionsData] = useState([]);
  const [contratsData, setContratsData] = useState([]);
  const [anneeMkt, setAnneeMkt] = useState("2024");
  
  // NOUVEAU: Import Journal QuickBooks
  const [journalData, setJournalData] = useState("");
  const [anneeJournal, setAnneeJournal] = useState("2024");

  // Import CSV Clients
  const [csvClients, setCsvClients] = useState("");

  // CA Cash Historique
  const [cashYear, setCashYear] = useState("2025");
  const [cashEntries, setCashEntries] = useState([
    { mois: 1, prqc: '', lmds: '', location: '' },
    { mois: 2, prqc: '', lmds: '', location: '' },
    { mois: 3, prqc: '', lmds: '', location: '' },
    { mois: 4, prqc: '', lmds: '', location: '' },
    { mois: 5, prqc: '', lmds: '', location: '' },
    { mois: 6, prqc: '', lmds: '', location: '' },
    { mois: 7, prqc: '', lmds: '', location: '' },
    { mois: 8, prqc: '', lmds: '', location: '' },
    { mois: 9, prqc: '', lmds: '', location: '' },
    { mois: 10, prqc: '', lmds: '', location: '' },
    { mois: 11, prqc: '', lmds: '', location: '' },
    { mois: 12, prqc: '', lmds: '', location: '' },
  ]);
  const [savingCash, setSavingCash] = useState(false);
  const MOIS_NOMS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
  const [csvClientsCount, setCsvClientsCount] = useState(0);

  const parseGestionMinute = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      toast.error("Format invalide - au moins 2 lignes requises");
      return null;
    }
    
    const headers = lines[0].split('\t');
    const parsed = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split('\t');
      if (values.length >= 7) {
        parsed.push({
          nom: values[0],
          date_debut: values[1],
          date_fin: values[2],
          heures_travaillees: values[3],
          tarif_horaire: parseFloat(values[4]) || 0,
          gains: parseFloat(values[5]) || 0,
          heures_totales: values[6]
        });
      }
    }
    
    return parsed;
  };

  const handleImportHeures = async () => {
    setLoading(true);
    try {
      const parsed = parseGestionMinute(heuresData);
      if (!parsed || parsed.length === 0) {
        toast.error("Aucune donnée valide trouvée");
        setLoading(false);
        return;
      }
      
      const response = await api.importHeuresHistoriques({
        annee: parseInt(anneeHeures),
        donnees: parsed
      });
      
      toast.success(`✅ ${parsed.length} entrées d'heures importées pour ${anneeHeures}`);
      setHeuresData("");
    } catch (error) {
      console.error("Erreur import heures:", error);
      toast.error("Erreur lors de l'import: " + error.message);
    }
    setLoading(false);
  };

  const handleImportMarketing = async () => {
    setLoading(true);
    try {
      const response = await api.importMarketingHistorique({
        annee: parseInt(anneeMkt),
        soumissions: soumissionsData,
        contrats: contratsData
      });
      
      toast.success(`✅ Données marketing importées pour ${anneeMkt}`);
      setSoumissionsData([]);
      setContratsData([]);
    } catch (error) {
      console.error("Erreur import marketing:", error);
      toast.error("Erreur lors de l'import: " + error.message);
    }
    setLoading(false);
  };

  const handleImportJournal = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${window.__API_URL__ || ""}/api/journal/import-paste`, {
        year: parseInt(anneeJournal)
      });
      
      const result = response.data;
      
      if (response.status === 200) {
        toast.success(`✅ ${result.imported} transactions Journal importées pour ${anneeJournal}!`);
        setJournalData("");
      } else {
        toast.error("Erreur import");
      }
    } catch (error) {
      console.error("Erreur import journal:", error);
      toast.error("Erreur lors de l'import");
    }
    setLoading(false);
  };

  const addSoumissionRow = () => {
    setSoumissionsData([...soumissionsData, { mois: 1, nombre: 0 }]);
  };

  const addContratRow = () => {
    setContratsData([...contratsData, { mois: 1, nombre: 0, valeur: 0 }]);
  };

  const updateSoumission = (index, field, value) => {
    const newData = [...soumissionsData];
    newData[index][field] = field === 'mois' ? parseInt(value) : parseInt(value);
    setSoumissionsData(newData);
  };

  const updateContrat = (index, field, value) => {
    const newData = [...contratsData];
    newData[index][field] = field === 'mois' ? parseInt(value) : parseFloat(value);
    setContratsData(newData);
  };

  const MONTHS = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  return (
    <div className="space-y-6">
      <div className="animate-fade-in">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Import de Données Historiques</h1>
        <p className="text-slate-500 mt-1">Importez vos données des années précédentes (2024, 2025...)</p>
      </div>

      <Card className="animate-fade-in delay-100">
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="heures" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Heures</span>
              </TabsTrigger>
              <TabsTrigger value="marketing" className="flex items-center gap-2">
                <Megaphone className="w-4 h-4" />
                <span className="hidden sm:inline">Marketing</span>
              </TabsTrigger>
              <TabsTrigger value="journal" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Journal QB</span>
              </TabsTrigger>
              <TabsTrigger value="cash" className="flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                <span className="hidden sm:inline">CA Cash</span>
              </TabsTrigger>
              <TabsTrigger value="clients" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Clients</span>
              </TabsTrigger>
            </TabsList>

            {/* TAB: Import Heures */}
            <TabsContent value="heures" className="space-y-6 mt-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-blue-900">Format Gestion Minute (TSV)</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      Copiez-collez les données depuis Gestion Minute. Format attendu (séparé par tabulations):
                    </p>
                    <pre className="bg-white p-2 rounded mt-2 text-xs overflow-x-auto">
Nom   Date début      Date fin        Heures travaillées      Tarif horaire   Gains   Heures totales{'\n'}
Gregory Poirier       2025-11-01 17:57        2025-11-30 17:57        120:21  40.53   4878.19 120:21
                    </pre>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="annee-heures">Année</Label>
                  <Input
                    id="annee-heures"
                    type="number"
                    value={anneeHeures}
                    onChange={(e) => setAnneeHeures(e.target.value)}
                    placeholder="2024"
                    className="max-w-xs"
                  />
                </div>

                <div>
                  <Label htmlFor="heures-input">Données Gestion Minute (copier-coller)</Label>
                  <Textarea
                    id="heures-input"
                    value={heuresData}
                    onChange={(e) => setHeuresData(e.target.value)}
                    placeholder="Collez ici les données copiées depuis Gestion Minute..."
                    className="min-h-64 font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {heuresData ? `${heuresData.split('\n').length - 1} lignes détectées` : "Aucune donnée"}
                  </p>
                </div>

                <Button 
                  onClick={handleImportHeures} 
                  disabled={loading || !heuresData}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? "Import en cours..." : "Importer les Heures"}
                  <Upload className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </TabsContent>

            {/* TAB: Import Marketing */}
            <TabsContent value="marketing" className="space-y-6 mt-6">
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-amber-900">Saisie Manuelle</h3>
                    <p className="text-sm text-amber-700 mt-1">
                      Entrez vos données de soumissions et contrats mois par mois pour l'année historique
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="annee-mkt">Année</Label>
                  <Input
                    id="annee-mkt"
                    type="number"
                    value={anneeMkt}
                    onChange={(e) => setAnneeMkt(e.target.value)}
                    placeholder="2024"
                    className="max-w-xs"
                  />
                </div>

                {/* Soumissions */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Soumissions Envoyées</CardTitle>
                      <Button onClick={addSoumissionRow} size="sm" variant="outline">
                        + Ajouter Mois
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {soumissionsData.length === 0 && (
                        <p className="text-sm text-slate-500 text-center py-4">
                          Cliquez sur "+ Ajouter Mois" pour commencer
                        </p>
                      )}
                      {soumissionsData.map((row, idx) => (
                        <div key={row.id || row._id || idx} className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Mois</Label>
                            <select 
                              value={row.mois}
                              onChange={(e) => updateSoumission(idx, 'mois', e.target.value)}
                              className="w-full border rounded px-3 py-2 text-sm"
                            >
                              {MONTHS.slice(1).map((m, i) => (
                                <option key={i+1} value={i+1}>{m}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">Nombre de soumissions</Label>
                            <Input
                              type="number"
                              value={row.nombre}
                              onChange={(e) => updateSoumission(idx, 'nombre', e.target.value)}
                              placeholder="0"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Contrats */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Contrats Signés</CardTitle>
                      <Button onClick={addContratRow} size="sm" variant="outline">
                        + Ajouter Mois
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {contratsData.length === 0 && (
                        <p className="text-sm text-slate-500 text-center py-4">
                          Cliquez sur "+ Ajouter Mois" pour commencer
                        </p>
                      )}
                      {contratsData.map((row, idx) => (
                        <div key={row.id || row._id || idx} className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Mois</Label>
                            <select 
                              value={row.mois}
                              onChange={(e) => updateContrat(idx, 'mois', e.target.value)}
                              className="w-full border rounded px-3 py-2 text-sm"
                            >
                              {MONTHS.slice(1).map((m, i) => (
                                <option key={i+1} value={i+1}>{m}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">Nombre de contrats</Label>
                            <Input
                              type="number"
                              value={row.nombre}
                              onChange={(e) => updateContrat(idx, 'nombre', e.target.value)}
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Valeur totale ($)</Label>
                            <Input
                              type="number"
                              value={row.valeur}
                              onChange={(e) => updateContrat(idx, 'valeur', e.target.value)}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Button 
                  onClick={handleImportMarketing} 
                  disabled={loading || (soumissionsData.length === 0 && contratsData.length === 0)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {loading ? "Import en cours..." : "Importer Données Marketing"}
                  <Upload className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </TabsContent>

            {/* TAB: Journal QuickBooks */}
            <TabsContent value="journal" className="space-y-6 mt-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-blue-900">Journal QuickBooks (Copier-Coller)</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      Ouvrez votre Journal Excel, sélectionnez TOUT (Ctrl+A), copiez (Ctrl+C) et collez ci-dessous.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="annee-journal">Année</Label>
                  <select
                    id="annee-journal"
                    value={anneeJournal}
                    onChange={(e) => setAnneeJournal(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="2024">2024</option>
                    <option value="2025">2025</option>
                    <option value="2026">2026</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="journal-input">Données Journal QuickBooks (copier-coller)</Label>
                  <Textarea
                    id="journal-input"
                    value={journalData}
                    onChange={(e) => setJournalData(e.target.value)}
                    placeholder="Collez ici toutes les lignes du Journal Excel (avec en-têtes)..."
                    className="min-h-80 font-mono text-xs"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {journalData ? `${journalData.split('\n').length} lignes détectées` : "Aucune donnée"}
                  </p>
                </div>

                <Button 
                  onClick={handleImportJournal} 
                  disabled={loading || !journalData}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? "Import en cours..." : `Importer Journal ${anneeJournal}`}
                  <Upload className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </TabsContent>

            {/* TAB: Import CSV Clients */}
            <TabsContent value="clients" className="space-y-6 mt-6">
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-green-900">Import Clients / Dossiers (CSV)</h3>
                    <p className="text-sm text-green-700 mt-1">
                      Importez vos clients historiques depuis un fichier CSV. Colonnes requises: nom, prenom, email, telephone, adresse, ville.
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Fichier CSV</Label>
                  <input
                    type="file"
                    accept=".csv,.txt"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        const text = ev.target.result;
                        setCsvClients(text);
                        const lines = text.trim().split('\n');
                        setCsvClientsCount(lines.length - 1);
                      };
                      reader.readAsText(file, 'UTF-8');
                    }}
                    className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                  />
                  {csvClientsCount > 0 && <p className="text-sm text-green-600 mt-1">{csvClientsCount} client(s) détecté(s)</p>}
                </div>
                <div>
                  <Label>Ou copier-coller le CSV</Label>
                  <Textarea
                    value={csvClients}
                    onChange={(e) => { setCsvClients(e.target.value); setCsvClientsCount(Math.max(0, e.target.value.split('\n').length - 1)); }}
                    placeholder={"nom,prenom,email,telephone,adresse,ville\nDupont,Jean,jean@mail.com,450-555-1234,123 rue St-Jean,Montréal"}
                    className="min-h-40 font-mono text-xs"
                  />
                </div>
                <Button
                  disabled={loading || !csvClients}
                  onClick={async () => {
                    setLoading(true);
                    try {
                      const lines = csvClients.trim().split('\n');
                      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
                      const clients = lines.slice(1).map(l => {
                        const vals = l.split(',');
                        return headers.reduce((obj, h, i) => { obj[h] = vals[i]?.trim() || ''; return obj; }, {});
                      });
                      const res = await axios.post(`${window.__API_URL__ || ""}/api/import/clients-csv`, { clients }, { headers });
                      if (res.status !== 200) throw new Error('Erreur serveur');
                      const d = res.data;
                      toast.success(`${d.created} client(s) importé(s)!`);
                      setCsvClients('');
                      setCsvClientsCount(0);
                    } catch (e) { toast.error('Erreur import: ' + e.message); }
                    finally { setLoading(false); }
                  }}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="import-clients-csv-btn"
                >
                  {loading ? 'Import en cours...' : 'Importer les clients'} <Upload className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </TabsContent>

            {/* TAB: CA Cash Historique */}
            <TabsContent value="cash" className="space-y-6 mt-6">
              <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded">
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-purple-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-purple-900">CA Argent Cash par mois</h3>
                    <p className="text-sm text-purple-700 mt-1">
                      Entrez les revenus cash par mois et par département pour les années précédentes.
                      Ces montants n'apparaissent pas dans QuickBooks.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Label>Année</Label>
                <select value={cashYear} onChange={e => setCashYear(e.target.value)} className="border rounded px-3 py-1.5 text-sm">
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                </select>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="p-2 text-left border">Mois</th>
                      <th className="p-2 text-right border">PRQC ($)</th>
                      <th className="p-2 text-right border">LMDS ($)</th>
                      <th className="p-2 text-right border">Location ($)</th>
                      <th className="p-2 text-right border font-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cashEntries.map((row, i) => (
                      <tr key={row.id || row._id || i} className="border-b">
                        <td className="p-2 border font-medium">{MOIS_NOMS[i]}</td>
                        <td className="p-1 border"><Input type="number" step="0.01" min="0" placeholder="0" className="h-8 text-right text-sm" value={row.prqc} onChange={e => { const u = [...cashEntries]; u[i].prqc = e.target.value; setCashEntries(u); }} /></td>
                        <td className="p-1 border"><Input type="number" step="0.01" min="0" placeholder="0" className="h-8 text-right text-sm" value={row.lmds} onChange={e => { const u = [...cashEntries]; u[i].lmds = e.target.value; setCashEntries(u); }} /></td>
                        <td className="p-1 border"><Input type="number" step="0.01" min="0" placeholder="0" className="h-8 text-right text-sm" value={row.location} onChange={e => { const u = [...cashEntries]; u[i].location = e.target.value; setCashEntries(u); }} /></td>
                        <td className="p-2 border text-right font-bold text-purple-700">{((parseFloat(row.prqc) || 0) + (parseFloat(row.lmds) || 0) + (parseFloat(row.location) || 0)).toFixed(2)} $</td>
                      </tr>
                    ))}
                    <tr className="bg-purple-50 font-bold">
                      <td className="p-2 border">TOTAL {cashYear}</td>
                      <td className="p-2 border text-right">{cashEntries.reduce((s, r) => s + (parseFloat(r.prqc) || 0), 0).toFixed(2)} $</td>
                      <td className="p-2 border text-right">{cashEntries.reduce((s, r) => s + (parseFloat(r.lmds) || 0), 0).toFixed(2)} $</td>
                      <td className="p-2 border text-right">{cashEntries.reduce((s, r) => s + (parseFloat(r.location) || 0), 0).toFixed(2)} $</td>
                      <td className="p-2 border text-right text-purple-800">{cashEntries.reduce((s, r) => s + (parseFloat(r.prqc) || 0) + (parseFloat(r.lmds) || 0) + (parseFloat(r.location) || 0), 0).toFixed(2)} $</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <Button
                onClick={async () => {
                  setSavingCash(true);
                  try {
                    const entries = cashEntries.filter(r => (parseFloat(r.prqc) || 0) + (parseFloat(r.lmds) || 0) + (parseFloat(r.location) || 0) > 0);
                    for (const row of entries) {
                      const depts = [
                        { dept: 'PRQC', amount: parseFloat(row.prqc) || 0 },
                        { dept: 'LMDS', amount: parseFloat(row.lmds) || 0 },
                        { dept: 'LOCATION', amount: parseFloat(row.location) || 0 },
                      ];
                      for (const { dept, amount } of depts) {
                        if (amount <= 0) continue;
                        await axios.post(`${API_URL}/api/cash/transactions`, {
                            type: 'revenue',
                            amount,
                            date: `${cashYear}-${String(row.mois).padStart(2, '0')}-15`,
                            description: `CA Cash ${row.mois}/${cashYear}`,
                            category: 'Vente'
                        });
                      }
                    }
                    toast.success(`CA Cash ${cashYear} importé avec succès!`);
                  } catch (e) { toast.error('Erreur import CA cash'); }
                  finally { setSavingCash(false); }
                }}
                disabled={savingCash}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {savingCash ? 'Enregistrement...' : `Enregistrer le CA Cash ${cashYear}`}
              </Button>
            </TabsContent>


          </Tabs>
        </CardContent>
      </Card>

      {/* Instructions supplémentaires */}
      <Card className="bg-slate-50">
        <CardHeader>
          <CardTitle className="text-base">📋 Instructions d'Import</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-slate-600">
          <div>
            <h4 className="font-medium text-slate-900 mb-1">Heures Employés:</h4>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Ouvrez Gestion Minute et exportez les données pour l'année souhaitée</li>
              <li>Sélectionnez tout le tableau et copiez (Ctrl+C ou Cmd+C)</li>
              <li>Collez directement dans la zone de texte ci-dessus</li>
              <li>Les données seront automatiquement converties en heures de pointage</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-slate-900 mb-1">Données Marketing:</h4>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Saisissez manuellement les soumissions et contrats pour chaque mois</li>
              <li>Ces données permettront les analyses historiques et les tendances YoY</li>
              <li>Vous pouvez importer plusieurs mois en une seule fois</li>
            </ul>
          </div>
          <div className="bg-amber-50 rounded p-3 border border-amber-200">
            <p className="text-amber-800">
              <strong>Note:</strong> Ces imports sont destinés aux années antérieures à l'utilisation du système de pointage actuel.
              Les données de 2026+ seront automatiquement collectées via le système de pointage.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImportHistorique;
